import type {
  Workspace,
  WorkspaceMember,
  Invitation,
  FloorPlanPermission,
} from '@/types/workspace';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';

// NOTE: This module only exists for legacy workspace records that pre-date the
// Clerk-organization flow. Once we confirm no projects depend on these tables
// we can safely delete the service/tests and rely on Clerk org APIs alone.

type Logger = typeof import('@/lib/logger').logger;
type SupabaseClient = Awaited<ReturnType<typeof getAuthenticatedSupabaseClient>>;
type AuthUtilsModule = typeof import('@/utils/getCurrentUserId');

let loggerPromise: Promise<Logger> | null = null;
const getLogger = async (): Promise<Logger> => {
  if (!loggerPromise) {
    loggerPromise = import('@/lib/logger').then(mod => mod.logger);
  }
  return loggerPromise;
};

let supabaseClientPromise: Promise<SupabaseClient> | null = null;
const getSupabaseClient = async (): Promise<SupabaseClient> => {
  if (!supabaseClientPromise) {
    supabaseClientPromise = getAuthenticatedSupabaseClient();
  }
  return supabaseClientPromise;
};

const getSupabaseClientAny = async (): Promise<any> =>
  (await getSupabaseClient()) as unknown as any;

let authUtilsPromise: Promise<AuthUtilsModule> | null = null;
const getAuthUtils = async (): Promise<AuthUtilsModule> => {
  if (!authUtilsPromise) {
    authUtilsPromise = import('@/utils/getCurrentUserId');
  }
  return authUtilsPromise;
};

// getCurrentUserId is now imported from utils

// ------------------------------------------------------------------------------------
// NOTE ON TYPE SAFETY
// ------------------------------------------------------------------------------------
// The generated Database types (`src/integrations/supabase/types.ts`) do not yet
// include the new collaboration tables (workspaces, workspace_members, etc.).
// Until the migrations are applied and the types are regenerated we cast the
// Supabase client to `any` when performing queries against these new tables to
// avoid TypeScript errors during compilation.  Once the database schema is
// updated we should regenerate the types with `supabase gen types typescript`
// and remove these casts.
// ------------------------------------------------------------------------------------

export const createWorkspace = async (
  name: string,
  description: string | null = null,
  userId?: string | null,
): Promise<Workspace | null> => {
  const [logger, authUtils, sb] = await Promise.all([
    getLogger(),
    getAuthUtils(),
    getSupabaseClientAny(),
  ]);
  // If userId not provided, try to get from Supabase auth
  const effectiveUserId = userId || (await authUtils.getCurrentUserId());
  if (!effectiveUserId) {
    logger.error('No authenticated user found while creating workspace');
    return null;
  }

  logger.log('Creating workspace with:', { name, description, owner_id: effectiveUserId });

  const { data, error } = await sb
    .from('workspaces')
    .insert({
      name,
      description,
      owner_id: effectiveUserId,
    })
    .select('*')
    .single();

  if (error) {
    logger.error('Error creating workspace:', error);
    logger.error('Error details:', {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
    });
    return null;
  }

  logger.log('Workspace created successfully:', data);

  // With Clerk, workspace members are managed through organizations
  // No need to add to workspace_members table

  return data as Workspace;
};

export const getWorkspaces = async (
  userId?: string | null,
  clerkOrgId?: string | null,
): Promise<Workspace[]> => {
  const [logger, authUtils, sb] = await Promise.all([
    getLogger(),
    getAuthUtils(),
    getSupabaseClientAny(),
  ]);
  // If Clerk org ID is provided, use organization service
  if (clerkOrgId) {
    const { getWorkspacesForClerkOrg } = await import('./organizationService');
    return getWorkspacesForClerkOrg(clerkOrgId);
  }

  // Otherwise fall back to Supabase auth
  const effectiveUserId = userId || (await authUtils.getCurrentUserId());
  if (!effectiveUserId) {
    logger.log('No authenticated user, returning empty workspaces');
    return [];
  }

  // Get workspaces where user is either owner OR a member
  // First approach: Get all workspaces where user is owner
  const { data: ownedWorkspaces, error: ownedError } = await sb
    .from('workspaces')
    .select('*')
    .eq('owner_id', effectiveUserId);

  if (ownedError) {
    logger.error('Error fetching owned workspaces:', ownedError);
  }

  // With Clerk, workspace members are managed through organizations
  // So we just return the owned workspaces
  const workspaceData = ownedWorkspaces || [];

  // Sort by created_at descending
  workspaceData.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return workspaceData as Workspace[];
};

export const getWorkspaceMembers = async (workspaceId: string): Promise<WorkspaceMember[]> => {
  const logger = await getLogger();
  // Legacy function - workspace members are now managed through Clerk organizations
  logger.warn('getWorkspaceMembers is deprecated - use Clerk organization members');
  return [];
};

export const inviteUserToWorkspace = async (
  email: string,
  workspaceId: string,
  role: 'admin' | 'editor' | 'viewer',
): Promise<Invitation | null> => {
  const [logger, authUtils, client] = await Promise.all([
    getLogger(),
    getAuthUtils(),
    getSupabaseClient(),
  ]);
  const sb = client as any;
  const supabase = client;
  const userId = await authUtils.getCurrentUserId();
  if (!userId) {
    logger.error('No authenticated user found while inviting user');
    return null;
  }

  // Generate a secure UUID token
  const token = (crypto as any).randomUUID
    ? (crypto as any).randomUUID()
    : (self.crypto?.randomUUID?.() ?? cryptoRandomFallback());

  const { data, error } = await sb
    .from('invitations')
    .upsert(
      {
        email,
        workspace_id: workspaceId,
        invited_by: userId,
        role,
        token,
      },
      { onConflict: 'workspace_id,email' },
    )
    .select('*')
    .single();

  if (error) {
    logger.error('Error creating invitation:', error);
    return null;
  }

  // ------------------------------------------------------------------
  // Fire off email via Edge Function
  try {
    const inviterEmail = (await authUtils.getCurrentUserEmail()) || 'no-reply';
    const appUrl = window.location.origin;
    await supabase.functions.invoke('send-invitation-email', {
      body: {
        email,
        workspaceName:
          (await sb.from('workspaces').select('name').eq('id', workspaceId).single()).data?.name ??
          'vfloow',
        inviterEmail,
        token,
        appUrl,
      },
    });
  } catch (fnErr) {
    logger.warn('Failed to invoke send-invitation-email function', fnErr);
  }

  return data as Invitation;
};

export const acceptInvitation = async (
  token: string,
): Promise<{ success: boolean; workspaceId?: string }> => {
  const [logger, authUtils, sb] = await Promise.all([
    getLogger(),
    getAuthUtils(),
    getSupabaseClientAny(),
  ]);
  const { data: invitation, error: findError } = await sb
    .from('invitations')
    .select('*')
    .eq('token', token)
    .single();

  if (findError || !invitation) {
    logger.error('Invitation not found:', findError);
    return { success: false };
  }

  if (new Date(invitation.expires_at) < new Date()) {
    logger.error('Invitation token has expired');
    return { success: false };
  }

  const userId = await authUtils.getCurrentUserId();
  if (!userId) {
    logger.error('No authenticated user while accepting invitation');
    return { success: false };
  }

  // Add user to workspace_members
  const { error: memberError } = await sb.from('workspace_members').insert({
    workspace_id: invitation.workspace_id,
    user_id: userId,
    role: invitation.role,
    invited_by: invitation.invited_by,
  });

  if (memberError) {
    logger.error('Error adding workspace member:', memberError);
    return { success: false };
  }

  // Mark invitation as accepted
  const { error: updateError } = await sb
    .from('invitations')
    .update({ accepted: true })
    .eq('id', invitation.id);

  if (updateError) {
    logger.error('Error updating invitation accepted flag:', updateError);
    return { success: false };
  }

  return { success: true, workspaceId: invitation.workspace_id };
};

export const removeWorkspaceMember = async (
  workspaceId: string,
  userId: string,
): Promise<boolean> => {
  const logger = await getLogger();
  // Legacy function - workspace members are now managed through Clerk organizations
  logger.warn('removeWorkspaceMember is deprecated - use Clerk organization management');
  return true;
};

export const updateMemberRole = async (
  workspaceId: string,
  userId: string,
  role: 'admin' | 'editor' | 'viewer',
): Promise<boolean> => {
  const logger = await getLogger();
  // Legacy function - workspace members are now managed through Clerk organizations
  logger.warn('updateMemberRole is deprecated - use Clerk organization management');
  return true;
};

export const updateWorkspace = async (
  workspaceId: string,
  updates: { name?: string; description?: string },
): Promise<Workspace | null> => {
  const [logger, sb] = await Promise.all([getLogger(), getSupabaseClientAny()]);
  const { data, error } = await sb
    .from('workspaces')
    .update(updates)
    .eq('id', workspaceId)
    .select('*')
    .single();

  if (error) {
    logger.error('Error updating workspace:', error);
    return null;
  }

  return data as Workspace;
};

export const assignFloorPlanToWorkspace = async (
  floorPlanId: string,
  workspaceId: string,
): Promise<boolean> => {
  const [logger, sb] = await Promise.all([getLogger(), getSupabaseClientAny()]);
  // Floor plans don't have workspace_id directly - they're linked through projects
  // This function might need to be deprecated or reimplemented
  logger.warn(
    'assignFloorPlanToWorkspace: Floor plans are linked to workspaces through projects, not directly',
  );

  // Get the floor plan's project first
  const { data: floorPlan, error: fpError } = await sb
    .from('floor_plans')
    .select('project_id')
    .eq('id', floorPlanId)
    .single();

  if (fpError || !floorPlan) {
    logger.error('Error fetching floor plan:', fpError);
    return false;
  }

  // Update the project's workspace_id instead
  const { error } = await sb
    .from('projects')
    .update({ workspace_id: workspaceId })
    .eq('id', floorPlan.project_id);

  if (error) {
    logger.error('Error assigning project to workspace:', error);
    return false;
  }
  return true;
};

export const grantFloorPlanPermission = async (
  floorPlanId: string,
  workspaceId: string,
  permissionLevel: 'edit' | 'view',
): Promise<FloorPlanPermission | null> => {
  const [logger, sb] = await Promise.all([getLogger(), getSupabaseClientAny()]);
  const { data, error } = await sb
    .from('floor_plan_permissions')
    .upsert(
      {
        floor_plan_id: floorPlanId,
        workspace_id: workspaceId,
        permission_level: permissionLevel,
      },
      { onConflict: 'floor_plan_id,workspace_id' },
    )
    .select('*')
    .single();

  if (error) {
    logger.error('Error upserting floor plan permission:', error);
    return null;
  }

  return data as FloorPlanPermission;
};

export const revokeFloorPlanPermission = async (
  floorPlanId: string,
  workspaceId: string,
): Promise<boolean> => {
  const [logger, sb] = await Promise.all([getLogger(), getSupabaseClientAny()]);
  const { error } = await sb
    .from('floor_plan_permissions')
    .delete()
    .eq('floor_plan_id', floorPlanId)
    .eq('workspace_id', workspaceId);

  if (error) {
    logger.error('Error revoking floor plan permission:', error);
    return false;
  }
  return true;
};

export const getPendingInvitations = async (workspaceId: string): Promise<Invitation[]> => {
  const [logger, sb] = await Promise.all([getLogger(), getSupabaseClientAny()]);
  const { data, error } = await sb
    .from('invitations')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('accepted', false);

  if (error) {
    logger.error('Error fetching pending invitations:', error);
    return [];
  }

  return (data || []) as Invitation[];
};

/**
 * Resend an invitation email by token / id. Reuses the existing edge function.
 */
export const resendInvitationEmail = async (invitationId: string): Promise<boolean> => {
  const [logger, authUtils, client] = await Promise.all([
    getLogger(),
    getAuthUtils(),
    getSupabaseClient(),
  ]);
  const sb = client as any;
  const supabase = client;
  const { data: invitation, error: fetchError } = await sb
    .from('invitations')
    .select('*')
    .eq('id', invitationId)
    .single();

  if (fetchError || !invitation) {
    logger.error('Failed to find invitation to resend:', fetchError);
    return false;
  }

  // Generate a fresh token and expiry (7 days from now)
  const generateUUID = () => {
    if ((crypto as any).randomUUID) return (crypto as any).randomUUID();
    if (typeof self !== 'undefined' && (self.crypto as any)?.randomUUID)
      return (self.crypto as any).randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const newToken = generateUUID();
  const newExpiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

  // Update invitation record
  const { error: updateErr } = await sb
    .from('invitations')
    .update({ token: newToken, expires_at: newExpiry, accepted: false })
    .eq('id', invitationId);

  if (updateErr) {
    logger.error('Failed to update invitation with new token/expiry:', updateErr);
    return false;
  }

  // Fetch workspace name for email template
  const { data: workspaceData } = await sb
    .from('workspaces')
    .select('name')
    .eq('id', invitation.workspace_id)
    .single();

  try {
    const inviterEmail = (await authUtils.getCurrentUserEmail()) || 'no-reply';
    const appUrl = window.location.origin;
    await supabase.functions.invoke('send-invitation-email', {
      body: {
        email: invitation.email,
        workspaceName: workspaceData?.name ?? 'vfloow',
        inviterEmail,
        token: newToken,
        appUrl,
      },
    });
    return true;
  } catch (err) {
    logger.error('Failed to resend invitation email:', err);
    return false;
  }
};

/**
 * Revoke (delete) an invitation.
 */
export const revokeInvitation = async (invitationId: string): Promise<boolean> => {
  const [logger, sb] = await Promise.all([getLogger(), getSupabaseClientAny()]);
  const { error } = await sb.from('invitations').delete().eq('id', invitationId);

  if (error) {
    logger.error('Error revoking invitation:', error);
    return false;
  }
  return true;
};

function cryptoRandomFallback() {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// ------------------------------------------------------------------------------------
// Workspace deletion helpers
// ------------------------------------------------------------------------------------

/**
 * Permanently deletes a workspace after optionally deleting its projects first.
 * This assumes ON DELETE CASCADE has been configured for child tables.
 */
export const deleteWorkspaceCascade = async (workspaceId: string): Promise<boolean> => {
  const [logger, sb] = await Promise.all([getLogger(), getSupabaseClientAny()]);
  // Delete the workspace – child rows should cascade if FK constraints are set.
  const { error } = await sb.from('workspaces').delete().eq('id', workspaceId);
  if (error) {
    logger.error('Failed to delete workspace:', error);
    return false;
  }
  return true;
};

/**
 * Moves all projects (and thus child entities) from one workspace to another, then deletes the old workspace.
 */
export const moveProjectsAndDelete = async (
  fromWorkspaceId: string,
  targetWorkspaceId: string,
): Promise<boolean> => {
  const [logger, sb] = await Promise.all([getLogger(), getSupabaseClientAny()]);
  // Move the projects first
  const { error: moveError } = await sb
    .from('projects')
    .update({ workspace_id: targetWorkspaceId })
    .eq('workspace_id', fromWorkspaceId);

  if (moveError) {
    logger.error('Failed to move projects to target workspace:', moveError);
    return false;
  }

  // Delete the old workspace
  return await deleteWorkspaceCascade(fromWorkspaceId);
};
